import fr from './locales/fr.json';
import de from './locales/de.json';

/**
 * i18n minimale — FR/DE, sans dépendance.
 *
 * Le jeu de clés est verrouillé par un test (`i18n.test.ts`) : une clé ajoutée d'un seul
 * côté casse le build. Sans ce test, l'exigence bilingue reste déclarative — une clé
 * oubliée ne se voit qu'en production, dans la langue qu'on parle le moins (Charte §4).
 *
 * ⚠️ L'allemand est celui de Suisse : jamais de « ß », toujours « ss ».
 */

export const LOCALES = { fr, de } as const;
export type Locale = keyof typeof LOCALES;
export type TranslationKey = keyof typeof fr;

const STORAGE_KEY = 'mnemo.locale';

export function detectLocale(): Locale {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === 'fr' || saved === 'de') return saved;
  } catch {
    // Stockage indisponible (navigation privée) : ce n'est pas une raison de tomber.
  }
  return navigator.language?.toLowerCase().startsWith('de') ? 'de' : 'fr';
}

export function persistLocale(locale: Locale): void {
  try {
    localStorage.setItem(STORAGE_KEY, locale);
  } catch {
    /* sans effet */
  }
}

export function translate(
  locale: Locale,
  key: TranslationKey,
  vars?: Record<string, string | number>,
): string {
  const template: string = LOCALES[locale][key] ?? LOCALES.fr[key] ?? key;
  if (!vars) return template;
  return template.replace(/\{(\w+)\}/g, (match, name: string) =>
    name in vars ? String(vars[name]) : match,
  );
}
